import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Col, Form, Row, Spin } from 'antd'
import { flatten, get, groupBy, intersectionWith, map, uniq } from 'lodash'
import PropTypes from 'prop-types'

import { areSameValue } from '../../../core/utils'

import {
  DirtyPromptForm,
  FormButtons,
  SearchableMetadataTree,
  SearchHighlightedSpan,
} from '../../../common/components/sections'
import {
  INGREDIENT_AUTOCHECK_TREE_HEIGHT,
  PERMISSIONS,
} from '../../../common/constants'
import { showErrorNotification } from '../../../common/helpers'
import { useFormDirty, useUserHasPermissions } from '../../../common/hooks'
import {
  idPropType,
  nodePropType,
  simpleEntityPropType,
} from '../../../common/prop-types'
import {
  flattenTreeStructure,
  getFormItemLabelAboveConfiguration,
} from '../../../common/utils'

import { useUpdateFoodMoodRelationsDetailsMutation } from '../../hooks'
import { foodMoodPropType } from '../../prop-types'
import { setFoodMoodPageIsDirty } from '../../redux/actions'

import { foodMoodRelationsDetailsFormInitialValues } from './initial-values'

const FoodMoodRelationsDetailsForm = ({ foodMood, dishTypeOptions }) => {
  const dispatch = useDispatch()

  const [form] = Form.useForm()
  const { isDirty, setToDirty, resetForm } = useFormDirty(form)

  const isFormEnabled = useUserHasPermissions([
    PERMISSIONS.FOOD_MOOD.SAVE_RELATIONS,
  ])

  const [
    updateFoodMoodIncludedFoodMoodsDetails,
    { isLoading: isSaveLoading, error: saveError },
  ] = useUpdateFoodMoodRelationsDetailsMutation()

  const initialValues = useMemo(
    () => foodMoodRelationsDetailsFormInitialValues(foodMood),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [foodMood.dishTypes],
  )

  const handleSaveData = useCallback(
    data => updateFoodMoodIncludedFoodMoodsDetails(foodMood.id, data),
    [foodMood.id, updateFoodMoodIncludedFoodMoodsDetails],
  )

  useEffect(() => {
    if (saveError) {
      showErrorNotification({
        title: 'Food Mood details error',
        description: saveError,
      })
    }
  }, [saveError])

  useEffect(() => {
    resetForm()
  }, [initialValues, resetForm])

  useEffect(() => {
    dispatch(setFoodMoodPageIsDirty({ isRelationsDirty: isDirty }))
  }, [dispatch, isDirty])

  return isSaveLoading ? (
    <Spin />
  ) : (
    <DirtyPromptForm
      isDirty={isDirty}
      form={form}
      initialValues={initialValues}
      name="relationsForm"
      onFinish={handleSaveData}
      autoComplete="off"
      onFieldsChange={setToDirty}
    >
      <Row>
        <Col span={12}>
          <FormButtons
            permissions={[PERMISSIONS.FOOD_MOOD.SAVE_RELATIONS]}
            isDisabled={!isDirty}
            onReset={resetForm}
          />
          <Form.Item
            name="dishTypes"
            label="Dish Types"
            labelCol={getFormItemLabelAboveConfiguration(true)}
          >
            <CheckableTree
              disabled={!isFormEnabled}
              treeData={dishTypeOptions}
            />
          </Form.Item>
        </Col>
      </Row>
    </DirtyPromptForm>
  )
}

FoodMoodRelationsDetailsForm.propTypes = {
  foodMood: foodMoodPropType.isRequired,
  dishTypeOptions: PropTypes.arrayOf(simpleEntityPropType).isRequired,
}

const CheckableTree = ({ treeData, value, onChange, disabled }) => {
  const [treeExpandedKeys, setTreeExpandedKeys] = useState([])
  const [treeCheckedKeys, setTreeCheckedKeys] = useState([])
  const flatTreeData = useMemo(() => flattenTreeStructure(treeData), [treeData])

  const groupedFlatTreeDataById = useMemo(
    () => groupBy(flatTreeData, 'id'),
    [flatTreeData],
  )

  const handleTreeOnExpand = useCallback(expandedKeys => {
    setTreeExpandedKeys(expandedKeys)
  }, [])

  const handleTreeOnCheck = useCallback(
    ({ checked }) => {
      const nodesToCheck = intersectionWith(
        flatTreeData,
        checked,
        ({ key }, checkedKey) => areSameValue(checkedKey, key),
      )

      return onChange(map(nodesToCheck, 'id'))
    },
    [flatTreeData, onChange],
  )

  const renderTreeNode = useCallback(
    (node, searchValue) => (
      <SearchHighlightedSpan searchValue={searchValue}>
        {node.name}
      </SearchHighlightedSpan>
    ),
    [],
  )

  useEffect(() => {
    const checkedNodes = map(value, id =>
      get(groupedFlatTreeDataById, `${id}[0]`),
    )

    setTreeExpandedKeys(uniq(flatten(map(checkedNodes, 'keyPath'))))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const checkedNodes = map(value, id =>
      get(groupedFlatTreeDataById, `${id}[0]`),
    )

    setTreeCheckedKeys(uniq(map(checkedNodes, 'key')))
  }, [value, flatTreeData, groupedFlatTreeDataById])

  return (
    <SearchableMetadataTree
      showSearch
      checkable
      disabled={disabled}
      // eslint-disable-next-line react/jsx-boolean-value
      checkStrictly
      checkedKeys={treeCheckedKeys}
      onCheck={handleTreeOnCheck}
      onExpand={handleTreeOnExpand}
      expandedKeys={treeExpandedKeys}
      titleRender={renderTreeNode}
      treeData={treeData}
      blockNode
      flatTreeData={flatTreeData}
      height={INGREDIENT_AUTOCHECK_TREE_HEIGHT}
    />
  )
}

CheckableTree.propTypes = {
  value: PropTypes.arrayOf(idPropType.isRequired),
  onChange: PropTypes.func,
  treeData: PropTypes.arrayOf(nodePropType),
  disabled: PropTypes.bool,
}
CheckableTree.defaultProps = {
  value: [],
  onChange: undefined,
  treeData: [],
  disabled: false,
}

export default FoodMoodRelationsDetailsForm
